const ACCENT = {
  praeambel: "#7B35B8",
  geltungsbereich: "#167B8A",
  grundsaetze: "#C0392B",
  vielfalt: "#228248",
  sicherheit: "#C07D10",
  respekt: "#B85211",
  konsequenzen: "#C01E7A",
  meldewege: "#3A6EBF",
};

const bodyStyle = { fontSize: "16px", lineHeight: 1.75, color: "rgba(10,9,7,0.85)", margin: 0 };
const listStyle = { display: "flex", flexDirection: "column", gap: "10px", margin: 0, paddingLeft: "20px" };
const h3Style = { fontSize: "15px", fontWeight: 600, letterSpacing: "0.02em", color: "var(--color-ink)", margin: 0 };
const metaStyle = { fontSize: "13px", color: "rgba(10,9,7,0.6)", margin: 0 };

function Section({ roman, title, accent, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "14px", borderLeft: `4px solid ${accent}`, paddingLeft: "22px" }}>
      <span style={{ fontSize: "11px", fontWeight: 600, letterSpacing: "0.14em", textTransform: "uppercase", color: accent }}>{roman}</span>
      <h2 style={{ fontFamily: "var(--font-serif)", fontSize: "22px", color: "var(--color-ink)", margin: 0 }}>{title}</h2>
      {children}
    </div>
  );
}

function Callout({ label, accent, children }) {
  return (
    <div
      style={{
        background: "rgba(10,9,7,0.03)",
        border: "1px solid rgba(10,9,7,0.1)",
        borderLeft: accent ? `3px solid ${accent}` : undefined,
        borderRadius: "8px",
        padding: "16px 20px",
        display: "flex",
        flexDirection: "column",
        gap: "6px",
      }}
    >
      <strong style={{ fontSize: "11px", letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--color-ink)" }}>{label}</strong>
      <div style={{ fontSize: "14px", lineHeight: 1.6, color: "rgba(10,9,7,0.8)" }}>{children}</div>
    </div>
  );
}

function Levels({ items }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
      {items.map((level) => (
        <div
          key={level.num}
          style={{
            display: "grid",
            gridTemplateColumns: "auto 1fr",
            gap: "14px",
            background: "rgba(10,9,7,0.03)",
            border: "1px solid rgba(10,9,7,0.1)",
            borderRadius: "8px",
            padding: "14px 18px",
          }}
        >
          <span style={{ fontFamily: "var(--font-serif)", fontSize: "20px", fontWeight: 700, color: ACCENT.konsequenzen }}>{level.num}</span>
          <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
            <strong style={{ fontSize: "14px", color: "var(--color-ink)" }}>{level.title}</strong>
            <span style={{ fontSize: "13px", color: "rgba(10,9,7,0.65)", lineHeight: 1.5 }}>{level.desc}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Real, already-authored content from konzepte/verhaltenskodex.html — not
 * paraphrased. English is a courtesy translation (unlike the
 * Gesellschaftsvertrag, this is not a notarized legal filing, so a full
 * translation is appropriate).
 */
export function VerhaltenskodexDe() {
  return (
    <div className="tl-card-light" style={{ display: "flex", flexDirection: "column", gap: "36px", padding: "44px 40px" }}>
      <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
        <p style={{ ...bodyStyle, fontFamily: "var(--font-serif)", fontStyle: "italic", fontSize: "17px" }}>
          für Vielfalt &amp; Toleranz im LARP
        </p>
      </div>

      <Section roman="I" title="Präambel" accent={ACCENT.praeambel}>
        <p style={bodyStyle}>
          LARP ist ein Raum für gemeinsames Geschichtenerzählen, Fantasie und Gemeinschaft. Wir bei Tales&amp;Legends
          glauben, dass dieser Raum dann am stärksten ist, wenn er für alle offen steht – unabhängig von Herkunft,
          Identität, Körper oder Lebensweise.
        </p>
        <p style={bodyStyle}>
          Dieser Verhaltenskodex ist kein Regelwerk aus Misstrauen, sondern ein gemeinsames Bekenntnis: Wir gestalten
          unsere Gemeinschaft aktiv so, dass sich alle Menschen sicher, willkommen und respektiert fühlen können.
        </p>
        <p style={bodyStyle}>
          Mit der Teilnahme an unseren Events, der Teilnahme an Aktivitäten der gUG oder der Nutzung unserer
          Online-Kanäle erkennt jede Person diesen Kodex an und verpflichtet sich, nach ihm zu handeln.
        </p>
      </Section>

      <Section roman="II" title="Geltungsbereich" accent={ACCENT.geltungsbereich}>
        <p style={bodyStyle}>Dieser Verhaltenskodex gilt für alle Bereiche, in denen unsere Gemeinschaft zusammenkommt:</p>
        <ul style={listStyle}>
          <li style={bodyStyle}>
            <strong>Events &amp; Cons:</strong> Auf dem gesamten Veranstaltungsgelände, in Auf- und Abbauzeiten sowie
            in angrenzenden Bereichen.
          </li>
          <li style={bodyStyle}>
            <strong>Veranstaltungen der gUG:</strong> Bei Treffen, Workshops, Proben und allen offiziellen Aktivitäten
            von Tales&amp;Legends gUG.
          </li>
          <li style={bodyStyle}>
            <strong>Online-Kanäle:</strong> In Discord-Servern, Foren, sozialen Medien und allen weiteren digitalen
            Kommunikationsräumen der Gemeinschaft.
          </li>
        </ul>
        <p style={bodyStyle}>
          Er gilt für alle Teilnehmenden, Veranstaltenden, Ehrenamtliche und Mitarbeitenden gleichermaßen. Keine
          Position schützt vor den Konsequenzen eines Verstoßes.
        </p>
      </Section>

      <Section roman="III" title="Grundsätze" accent={ACCENT.grundsaetze}>
        <p style={bodyStyle}>Alle weiteren Regeln in diesem Kodex leiten sich aus drei Grundsätzen ab:</p>
        <h3 style={h3Style}>Würde</h3>
        <p style={bodyStyle}>
          Jeder Mensch verdient es, mit Würde und Respekt behandelt zu werden – in der Realität ebenso wie im Spiel.
          Kein Charakter, kein Rollenspiel und kein Szenario rechtfertigt, eine Person tatsächlich zu erniedrigen, zu
          verletzen oder auszugrenzen.
        </p>
        <h3 style={h3Style}>Einverständnis</h3>
        <p style={bodyStyle}>
          Einverständnis ist die Grundlage jeder Interaktion. Es muss aktiv, informiert und jederzeit widerrufbar
          sein. Im Zweifel gilt: Fragen ist stärker als Annehmen.
        </p>
        <h3 style={h3Style}>Verantwortung</h3>
        <p style={bodyStyle}>
          Wir sind gemeinsam für den Raum verantwortlich, den wir schaffen. Wer eine Grenzüberschreitung wahrnimmt,
          trägt Verantwortung – gegenüber der betroffenen Person, der Gemeinschaft und sich selbst.
        </p>
      </Section>

      <Section roman="IV" title="Vielfalt &amp; Inklusion" accent={ACCENT.vielfalt}>
        <p style={bodyStyle}>
          LARP ist BUNT – und das ist eine Stärke. Diskriminierung, Ausgrenzung oder Herabsetzung aufgrund folgender
          Merkmale haben in unserer Gemeinschaft keinen Platz:
        </p>
        <ul style={listStyle}>
          <li style={bodyStyle}>Geschlecht, Geschlechtsidentität und Geschlechtsausdruck</li>
          <li style={bodyStyle}>Sexuelle Orientierung</li>
          <li style={bodyStyle}>Herkunft, Nationalität und ethnische Zugehörigkeit</li>
          <li style={bodyStyle}>Religion und Weltanschauung</li>
          <li style={bodyStyle}>Behinderung, chronische Erkrankung oder Neurodivergenz</li>
          <li style={bodyStyle}>Alter, Körper oder Äußeres</li>
          <li style={bodyStyle}>Sozioökonomischer Status</li>
        </ul>
        <h3 style={h3Style}>Darstellung im Spiel</h3>
        <p style={bodyStyle}>
          LARP lebt von Fiktion und dem Spiel mit Rollen. Dennoch gilt: Figuren oder Szenarien, die reale
          Diskriminierungsformen reproduzieren oder verherrlichen, widersprechen diesem Kodex. Wer Themen wie
          Rassismus, Ableismus oder Queerfeindlichkeit im Spiel berührt, ist verantwortlich für eine respektvolle
          Umsetzung und sollte betroffene Personen vorab ansprechen.
        </p>
        <h3 style={h3Style}>Barrierefreiheit</h3>
        <p style={bodyStyle}>
          Wir setzen uns aktiv dafür ein, Events und Inhalte so zu gestalten, dass Menschen mit unterschiedlichen
          Bedürfnissen teilnehmen können. Anliegen zur Barrierefreiheit können jederzeit an die Orga gerichtet werden.
        </p>
      </Section>

      <Section roman="V" title="Sicherheit &amp; Grenzen" accent={ACCENT.sicherheit}>
        <Callout label="Sicherheitswörter" accent={ACCENT.sicherheit}>
          Drei Wörter beenden jede Interaktion sofort und ohne Fragen:
          <br />
          <br />
          <strong>„STOP"</strong> — vollständiger Abbruch, alle treten zurück, keine Fragen.
          <br />
          <strong>„Wirklich Wirklich"</strong> oder <strong>„Oh Mutter"</strong> — ich meine das jetzt als echte
          Person, nicht als Figur.
          <br />
          <br />
          Wer eines dieser Wörter ausspricht, hat das Recht auf sofortigen Abstand und Stille. Die Orga und alle
          Beteiligten respektieren dies bedingungslos.
        </Callout>
        <h3 style={h3Style}>Körperlicher Kontakt</h3>
        <p style={bodyStyle}>
          Kein Körperkontakt ohne vorheriges Einverständnis – auch im Spiel. Umarmungen, Kämpfe oder andere
          körperliche Interaktionen bedürfen der Zustimmung aller Beteiligten. Ein „Nein" oder „Lieber nicht" ist
          immer akzeptabel und bedarf keiner Begründung.
        </p>
        <h3 style={h3Style}>Kampf &amp; Spielzeugwaffen</h3>
        <ul style={listStyle}>
          <li style={bodyStyle}>Kämpfe finden nur zwischen Personen statt, die sich gegenseitig dazu bereit erklärt haben.</li>
          <li style={bodyStyle}>Schläge auf Kopf, Hals, Wirbelsäule und Genitalbereich sind verboten.</li>
          <li style={bodyStyle}>Stiche sind nur mit ausdrücklich als stichfest zugelassenen Waffen erlaubt.</li>
          <li style={bodyStyle}>
            Wer aus dem Spiel aussteigen möchte, zeigt dies mit der Out-of-Game-Geste: Arme vor dem Körper zu einem X
            verschränken.
          </li>
        </ul>
        <h3 style={h3Style}>Substanzen</h3>
        <p style={bodyStyle}>
          Wer unter dem Einfluss von Alkohol oder anderen Substanzen steht, nimmt nicht am Kampf teil und ist für
          seine Handlungen gegenüber anderen vollständig verantwortlich. Bei erkennbarem Kontrollverlust greift die
          Orga ein.
        </p>
      </Section>

      <Section roman="VI" title="Respekt &amp; Umgang" accent={ACCENT.respekt}>
        <h3 style={h3Style}>IC und OOC</h3>
        <p style={bodyStyle}>
          Im LARP unterscheiden wir zwischen der Figur (In Character, IC) und der Person dahinter (Out of Character,
          OOC). Was die Figur erlebt oder tut, sagt nichts über die spielende Person aus. Aussagen, die eine Person
          OOC verletzen, sind auch dann inakzeptabel, wenn sie „im Charakter" gemacht werden.
        </p>
        <h3 style={h3Style}>Kommunikation und Sprache</h3>
        <ul style={listStyle}>
          <li style={bodyStyle}>Wir sprechen miteinander, nicht übereinander.</li>
          <li style={bodyStyle}>
            Beleidigungen, Beschimpfungen, gezielte Gerüchte und Mobbing werden nicht geduldet – weder persönlich noch
            online.
          </li>
          <li style={bodyStyle}>Kritik an Handlungen ist erlaubt; persönliche Angriffe auf Menschen sind es nicht.</li>
          <li style={bodyStyle}>Wir achten auf Sprache, die alle einschließt.</li>
        </ul>
        <h3 style={h3Style}>Konflikte</h3>
        <p style={bodyStyle}>
          Konflikte gehören zu jeder Gemeinschaft. Wir erwarten von allen Beteiligten, Meinungsverschiedenheiten
          sachlich und konstruktiv anzugehen. Bei unüberbrückbaren Konflikten stehen die Ansprechpersonen der Orga
          vermittelnd zur Verfügung.
        </p>
        <h3 style={h3Style}>Online-Netiquette</h3>
        <ul style={listStyle}>
          <li style={bodyStyle}>Auch in Online-Räumen gilt dieser Kodex vollständig.</li>
          <li style={bodyStyle}>
            Screenshots aus internen Gesprächen werden nicht ohne Zustimmung aller Beteiligten geteilt.
          </li>
          <li style={bodyStyle}>Provokationen und Trolle werden nicht befeuert – Meldefunktionen werden genutzt.</li>
        </ul>
      </Section>

      <Section roman="VII" title="Konsequenzen" accent={ACCENT.konsequenzen}>
        <p style={bodyStyle}>
          Verstöße gegen diesen Kodex werden ernst genommen. Die Konsequenzen richten sich nach Schwere und Häufigkeit
          des Verstoßes und werden durch die Orga oder die Geschäftsführung von Tales&amp;Legends gUG festgelegt.
          Schwere Verstöße können Stufen überspringen.
        </p>
        <Levels
          items={[
            { num: "I", title: "Gespräch", desc: "Ein klärendes Gespräch mit einer Ansprechperson. Ziel ist Verständnis und Verhaltensänderung, keine Bestrafung." },
            { num: "II", title: "Formelle Verwarnung", desc: "Schriftliche Verwarnung, die von der Geschäftsführung dokumentiert wird. Wiederholung führt zur nächsten Stufe." },
            { num: "III", title: "Befristeter Ausschluss", desc: "Ausschluss von einzelnen Events oder zeitweises Verbot der Nutzung von Online-Kanälen. Dauer je nach Schwere des Verstoßes." },
            { num: "IV", title: "Dauerhafter Ausschluss", desc: "Permanenter Ausschluss aus der Gemeinschaft bei schwerwiegenden oder wiederholten Verstößen. Bei strafrechtlich relevantem Verhalten werden Behörden eingeschaltet." },
          ]}
        />
        <Callout label="Fairness">
          Bevor Konsequenzen ausgesprochen werden, erhalten alle Beteiligten die Möglichkeit, gehört zu werden.
          Entscheidungen werden von mindestens zwei Personen der Orga oder der Geschäftsführung gemeinsam getroffen.
        </Callout>
      </Section>

      <Section roman="VIII" title="Meldewege &amp; Ansprechpersonen" accent={ACCENT.meldewege}>
        <p style={bodyStyle}>
          Wer einen Verstoß erlebt oder beobachtet, sollte diesen melden – auch wenn Unsicherheit besteht, ob es
          „schlimm genug" ist. Kein Anliegen ist zu klein.
        </p>
        <h3 style={h3Style}>Auf Events</h3>
        <ul style={listStyle}>
          <li style={bodyStyle}>Jede*r Orga-Mitarbeitende mit sichtbarem Orga-Kennzeichen ist Ansprechperson.</li>
          <li style={bodyStyle}>Ansprechpersonen sind während des gesamten Events erreichbar.</li>
        </ul>
        <h3 style={h3Style}>Online &amp; schriftlich</h3>
        <Callout label="E-Mail" accent={ACCENT.meldewege}>
          <a href="mailto:kodex@talesandlegends.org">kodex@talesandlegends.org</a> – vertraulich, wird nur von
          vertrauensvollen Orga-Mitgliedern gelesen.
        </Callout>
        <h3 style={h3Style}>Vertraulichkeit</h3>
        <p style={bodyStyle}>
          Meldungen werden vertraulich behandelt. Wer eine Meldung macht, hat das Recht, anonym zu bleiben. Wir
          schützen meldende Personen vor Repressalien.
        </p>
        <p style={bodyStyle}>Wir bitten darum, Vorfälle zeitnah zu melden – je früher, desto besser können wir handeln.</p>
      </Section>

      <div style={{ borderTop: "1px solid rgba(10,9,7,0.12)", paddingTop: "24px", display: "flex", flexDirection: "column", gap: "8px" }}>
        <p style={metaStyle}>
          Dieser Verhaltenskodex wird regelmäßig überprüft und bei Bedarf angepasst. Feedback und Anregungen nimmt die
          Geschäftsführung jederzeit entgegen.
        </p>
      </div>
    </div>
  );
}

/** Courtesy translation — German is the primary/authoritative wording (see VerhaltenskodexDe). */
export function VerhaltenskodexEn() {
  return (
    <div className="tl-card-light" style={{ display: "flex", flexDirection: "column", gap: "36px", padding: "44px 40px" }}>
      <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
        <p style={{ ...bodyStyle, fontFamily: "var(--font-serif)", fontStyle: "italic", fontSize: "17px" }}>
          for diversity &amp; tolerance in LARP
        </p>
      </div>

      <Section roman="I" title="Preamble" accent={ACCENT.praeambel}>
        <p style={bodyStyle}>
          LARP is a space for shared storytelling, imagination, and community. At Tales&amp;Legends, we believe this
          space is strongest when it's open to everyone — regardless of background, identity, body, or way of life.
        </p>
        <p style={bodyStyle}>
          This code of conduct isn't a rulebook born of distrust — it's a shared commitment: we actively shape our
          community so that everyone can feel safe, welcome, and respected.
        </p>
        <p style={bodyStyle}>
          By taking part in our events, participating in the gUG's activities, or using our online channels, every
          person acknowledges this code and commits to acting in accordance with it.
        </p>
      </Section>

      <Section roman="II" title="Scope" accent={ACCENT.geltungsbereich}>
        <p style={bodyStyle}>This code of conduct applies everywhere our community comes together:</p>
        <ul style={listStyle}>
          <li style={bodyStyle}>
            <strong>Events &amp; conventions:</strong> across the entire event site, during setup and teardown, and
            in adjacent areas.
          </li>
          <li style={bodyStyle}>
            <strong>gUG activities:</strong> at meetings, workshops, rehearsals, and all official activities of
            Tales&amp;Legends gUG.
          </li>
          <li style={bodyStyle}>
            <strong>Online channels:</strong> on Discord servers, forums, social media, and any other digital
            communication space of the community.
          </li>
        </ul>
        <p style={bodyStyle}>
          It applies equally to all participants, organizers, volunteers, and staff. No position exempts anyone from
          the consequences of a violation.
        </p>
      </Section>

      <Section roman="III" title="Principles" accent={ACCENT.grundsaetze}>
        <p style={bodyStyle}>Every other rule in this code follows from three principles:</p>
        <h3 style={h3Style}>Dignity</h3>
        <p style={bodyStyle}>
          Every person deserves to be treated with dignity and respect — in reality just as much as in play. No
          character, no roleplay, and no scenario justifies actually humiliating, hurting, or excluding a person.
        </p>
        <h3 style={h3Style}>Consent</h3>
        <p style={bodyStyle}>
          Consent is the foundation of every interaction. It must be active, informed, and revocable at any time.
          When in doubt: asking beats assuming.
        </p>
        <h3 style={h3Style}>Responsibility</h3>
        <p style={bodyStyle}>
          We are jointly responsible for the space we create. Anyone who notices a boundary being crossed carries
          responsibility — toward the person affected, the community, and themselves.
        </p>
      </Section>

      <Section roman="IV" title="Diversity &amp; Inclusion" accent={ACCENT.vielfalt}>
        <p style={bodyStyle}>
          LARP is colorful — and that's a strength. Discrimination, exclusion, or belittlement based on the following
          characteristics has no place in our community:
        </p>
        <ul style={listStyle}>
          <li style={bodyStyle}>Gender, gender identity, and gender expression</li>
          <li style={bodyStyle}>Sexual orientation</li>
          <li style={bodyStyle}>Origin, nationality, and ethnicity</li>
          <li style={bodyStyle}>Religion and worldview</li>
          <li style={bodyStyle}>Disability, chronic illness, or neurodivergence</li>
          <li style={bodyStyle}>Age, body, or appearance</li>
          <li style={bodyStyle}>Socioeconomic status</li>
        </ul>
        <h3 style={h3Style}>Portrayal in-game</h3>
        <p style={bodyStyle}>
          LARP lives on fiction and playing with roles. Even so: characters or scenarios that reproduce or glorify
          real forms of discrimination contradict this code. Anyone touching on themes like racism, ableism, or
          queerphobia in play is responsible for a respectful execution and should speak with affected people
          beforehand.
        </p>
        <h3 style={h3Style}>Accessibility</h3>
        <p style={bodyStyle}>
          We actively work to design events and content so that people with different needs can take part. Questions
          about accessibility can be raised with the organizing team at any time.
        </p>
      </Section>

      <Section roman="V" title="Safety &amp; Boundaries" accent={ACCENT.sicherheit}>
        <Callout label="Safe words" accent={ACCENT.sicherheit}>
          Three words end any interaction immediately and without question:
          <br />
          <br />
          <strong>"STOP"</strong> — full stop, everyone steps back, no questions asked.
          <br />
          <strong>"Wirklich Wirklich" ("Really Really")</strong> or <strong>"Oh Mutter" ("Oh Mother")</strong> — I
          mean this as a real person now, not as my character.
          <br />
          <br />
          Anyone who says one of these words has the right to immediate space and silence. The organizing team and
          everyone present respect this unconditionally.
        </Callout>
        <h3 style={h3Style}>Physical contact</h3>
        <p style={bodyStyle}>
          No physical contact without prior consent — even in play. Hugs, fights, or other physical interactions
          require the agreement of everyone involved. A "no" or "rather not" is always acceptable and needs no
          justification.
        </p>
        <h3 style={h3Style}>Combat &amp; prop weapons</h3>
        <ul style={listStyle}>
          <li style={bodyStyle}>Fights only take place between people who have both agreed to it.</li>
          <li style={bodyStyle}>Hits to the head, neck, spine, and groin are forbidden.</li>
          <li style={bodyStyle}>Thrusts are only allowed with weapons explicitly approved as thrust-safe.</li>
          <li style={bodyStyle}>
            Anyone wanting to step out of the game shows this with the out-of-game gesture: crossing your arms in
            front of your body to form an X.
          </li>
        </ul>
        <h3 style={h3Style}>Substances</h3>
        <p style={bodyStyle}>
          Anyone under the influence of alcohol or other substances does not take part in combat and is fully
          responsible for their actions toward others. If a visible loss of control occurs, the organizing team steps
          in.
        </p>
      </Section>

      <Section roman="VI" title="Respect &amp; Conduct" accent={ACCENT.respekt}>
        <h3 style={h3Style}>IC and OOC</h3>
        <p style={bodyStyle}>
          In LARP, we distinguish between the character (In Character, IC) and the person behind it (Out of
          Character, OOC). What a character experiences or does says nothing about the person playing it. Statements
          that hurt a person OOC are unacceptable even when made "in character."
        </p>
        <h3 style={h3Style}>Communication and language</h3>
        <ul style={listStyle}>
          <li style={bodyStyle}>We talk with each other, not about each other.</li>
          <li style={bodyStyle}>
            Insults, name-calling, targeted rumors, and bullying are not tolerated — neither in person nor online.
          </li>
          <li style={bodyStyle}>Criticizing actions is fine; personal attacks on people are not.</li>
          <li style={bodyStyle}>We pay attention to language that includes everyone.</li>
        </ul>
        <h3 style={h3Style}>Conflicts</h3>
        <p style={bodyStyle}>
          Conflict is part of every community. We expect everyone involved to approach disagreements calmly and
          constructively. For conflicts that can't be resolved directly, the organizing team's contact people are
          available to mediate.
        </p>
        <h3 style={h3Style}>Online netiquette</h3>
        <ul style={listStyle}>
          <li style={bodyStyle}>This code applies fully in online spaces too.</li>
          <li style={bodyStyle}>Screenshots from private conversations are not shared without everyone involved agreeing.</li>
          <li style={bodyStyle}>Provocations and trolls don't get fed — reporting features get used instead.</li>
        </ul>
      </Section>

      <Section roman="VII" title="Consequences" accent={ACCENT.konsequenzen}>
        <p style={bodyStyle}>
          Violations of this code are taken seriously. Consequences depend on the severity and frequency of the
          violation and are decided by the organizing team or the management of Tales&amp;Legends gUG. Serious
          violations can skip stages.
        </p>
        <Levels
          items={[
            { num: "I", title: "Conversation", desc: "A clarifying conversation with a contact person. The goal is understanding and a change in behavior, not punishment." },
            { num: "II", title: "Formal warning", desc: "A written warning documented by management. Repetition leads to the next stage." },
            { num: "III", title: "Temporary exclusion", desc: "Exclusion from individual events, or a temporary ban from using online channels. Duration depends on the severity of the violation." },
            { num: "IV", title: "Permanent exclusion", desc: "Permanent exclusion from the community for serious or repeated violations. Authorities are involved where behavior is criminally relevant." },
          ]}
        />
        <Callout label="Fairness">
          Before consequences are decided, everyone involved gets the chance to be heard. Decisions are made jointly
          by at least two people from the organizing team or management.
        </Callout>
      </Section>

      <Section roman="VIII" title="Reporting &amp; Contacts" accent={ACCENT.meldewege}>
        <p style={bodyStyle}>
          If you experience or witness a violation, you should report it — even if you're unsure whether it's
          "serious enough." No concern is too small.
        </p>
        <h3 style={h3Style}>At events</h3>
        <ul style={listStyle}>
          <li style={bodyStyle}>Any organizing team member wearing a visible organizer badge is a contact person.</li>
          <li style={bodyStyle}>Contact people are reachable throughout the entire event.</li>
        </ul>
        <h3 style={h3Style}>Online &amp; in writing</h3>
        <Callout label="Email" accent={ACCENT.meldewege}>
          <a href="mailto:kodex@talesandlegends.org">kodex@talesandlegends.org</a> — confidential, read only by
          trusted members of the organizing team.
        </Callout>
        <h3 style={h3Style}>Confidentiality</h3>
        <p style={bodyStyle}>
          Reports are handled confidentially. Anyone making a report has the right to remain anonymous. We protect
          people who report from retaliation.
        </p>
        <p style={bodyStyle}>We ask that incidents be reported promptly — the sooner we know, the sooner we can act.</p>
      </Section>

      <div style={{ borderTop: "1px solid rgba(10,9,7,0.12)", paddingTop: "24px", display: "flex", flexDirection: "column", gap: "8px" }}>
        <p style={metaStyle}>
          This code of conduct is reviewed regularly and adjusted as needed. Management welcomes feedback and
          suggestions at any time.
        </p>
      </div>
    </div>
  );
}
